import React, { useState } from 'react';
import axios from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BASE_URL, POST_PLACE } from '../utlilities/config';
import { ToastContainer, toast, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiX, FiMap, FiSave, FiMapPin } from 'react-icons/fi';

// Spinner component to match other modals
const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Processing...</p>
    </div>
  </div>
);

// Form field component
const FormField = ({ label, required, error, touched, children }) => (
  <div className="w-full">
    <div className="flex justify-between mb-1.5">
      <label className="text-sm font-medium text-gray-700 flex items-center">
        {label} {required && <span className="text-red-500 ml-1">*</span>}
      </label>
    </div>
    {children}
    {touched && error && (
      <p className="mt-1 text-sm text-red-500 animate-fade-in">{error}</p>
    )}
  </div>
);

const NewPlace = ({ setPlaceModal, refreshData, onPlaceSaved, placeToEdit }) => {
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('accessToken');

  const isEditing = Boolean(placeToEdit);

  const formik = useFormik({
    initialValues: {
      name: placeToEdit?.PlaceName || '',
      ID: placeToEdit?.PlaceID || '',
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required('Place Name is required'),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const response = await axios.post(`${BASE_URL}${POST_PLACE}`, {}, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Name': values.name,
            'ID': values.ID,
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.status === 200) {
          toast.success(`Place ${isEditing ? 'updated' : 'added'} successfully!`, {
            autoClose: 1000,
          });
          const newPlace = { PlaceName: values.name };
          onPlaceSaved(newPlace);
          setPlaceModal(false);
          refreshData();
        }
      } catch (error) {
        console.error(error);
        toast.error(`Failed to ${isEditing ? 'update' : 'add'} place. Please try again.`, {
          autoClose: 1000,
        });
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      
      {loading && <Spinner />}
      
      <div className="bg-white w-3/5 max-w-xl rounded-2xl shadow-2xl transform transition-transform duration-300 ease-out animate-zoom-in overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-5 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <FiMapPin className="mr-2" /> {isEditing ? 'Edit Place' : 'Add New Place'}
          </h2>
          <button 
            onClick={() => setPlaceModal(false)}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors duration-200"
            aria-label="Close modal"
          >
            <FiX size={20} />
          </button>
        </div>
        
        <form onSubmit={formik.handleSubmit} className="p-8 space-y-6">
          <FormField 
            label="Place Name" 
            required={true}
            error={formik.errors.name}
            touched={formik.touched.name}
          >
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                <FiMap />
              </div>
              <input
                type="text"
                name="name"
                placeholder="Enter Place Name"
                className={`w-full pl-10 pr-3 py-3 border ${
                  formik.touched.name && formik.errors.name 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                    : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                value={formik.values.name}
                onChange={(e) => {
                  const value = e.target.value || '';
                  formik.setFieldValue('name', value.toUpperCase());
                }}
                onBlur={formik.handleBlur}
                autoFocus
              />
            </div>
          </FormField>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-100">
            <button
              type="button"
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 transition-colors duration-200 flex items-center"
              onClick={() => setPlaceModal(false)}
            >
              <FiX className="mr-2" /> Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
            >
              <FiSave className="mr-2" /> {isEditing ? 'Update Place' : 'Save Place'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewPlace;
